// The row-level actions previously inlined in OpportunityRow, extracted so they can be wired into the DS
// Table's ActionColumn / DateOrAddColumn from the page and unit-tested in isolation (by mocking the modal +
// mutation modules). Each takes the plain OpportunityNode plus an onDone refetch callback, called once the
// mutation has succeeded.

import React from 'react';
import {FormattedMessage} from 'react-intl';
import {Modal, Alert} from '@forecast-it/design-system';
import util from '../shared/util/util';
import {showModal, MODAL_TYPE} from '../shared/components/modals/generic_modal_conductor';
import {ConfirmationModalPrimaryButton} from '../shared/components/modals/general/ConfirmationModal';
import {createToast} from '../shared/components/toasts/toast';
import {changeWinChance} from '../project-tab/projects/project-settings/ProjectSettingsMutations';
import {bandForWinChance, toPercent, type StageBand} from './opportunityStage';
import DeleteProjectMutation from '../../mutations/delete_project_mutation';
import {SOCKET_ACTION} from '../../constants';
import type {OpportunityNode} from './opportunityTypes';
import {isProjectOpportunity} from '../shared/util/projectStageUtil';

// "<win probability %> (<stage>)" for a win chance, e.g. "40% (Qualified)" — the same phrasing the Update
// Win Probability modal uses in its confirmation banner, so the quick actions read identically. The
// percentage is the opportunity's own win chance; Lost and Won read as their fixed 0% / 100%.
const bandStage = (wp: number, formatMessage: (descriptor: {id: string}) => string, bands: StageBand[]): string => {
	const resolved = bandForWinChance(bands, wp);
	if (resolved.kind === 'band') {
		return `${toPercent(wp)}% (${resolved.band.name})`;
	}
	if (resolved.kind === 'lost') {
		return `0% (${formatMessage({id: 'opportunities.stage.lost'})})`;
	}
	if (resolved.kind === 'won') {
		return `100% (${formatMessage({id: 'opportunities.stage.won'})})`;
	}
	return '';
};

// Set Win Probability to 100% (→ Won) or 0% (→ Lost) straight from the kebab, without opening the modal.
// Commits the same changeWinChance mutation the Update Win Probability modal uses (a 0..1 fraction) and, once the
// save succeeds, shows the shared green success banner naming the old → new band + stage and refetches so the
// row re-buckets into the Won / Lost tab. formatMessage is injected (rather than hooked) so this stays a plain, testable helper.
export function setWinProbability(
	node: OpportunityNode,
	winChance: number,
	onDone: () => void,
	formatMessage: (descriptor: {id: string}, values?: Record<string, string>) => string,
	bands: StageBand[]
): void {
	// A won opportunity is a converted project (status ≠ OPPORTUNITY) whose win chance the backend always
	// reports as 100%; re-open it to OPPORTUNITY so a move to a lower chance actually persists. Setting it
	// straight to 100% changes nothing, so there is nothing to re-open.
	const reopenToOpportunity = !isProjectOpportunity(node) && winChance !== 1;
	// Read the old band now: the refetch replaces the row's win chance.
	const message = formatMessage(
		{id: 'opportunities.update_win_probability.toast'},
		{
			project: node.name ?? '',
			from: bandStage(node.baselineWinChance ?? 1, formatMessage, bands),
			to: bandStage(winChance, formatMessage, bands),
		}
	);
	// Refetch only once the save has succeeded; a refetch started alongside it can land last with the old value.
	changeWinChance(node.id, winChance, reopenToOpportunity, () => {
		createToast({duration: 5000, variant: 'banner', message: <Alert type={Alert.TYPE.SUCCESS} text={message} />});
		onDone();
	});
}

// Delete opens the shared type-to-confirm confirmation modal — the same one the client Contacts list uses,
// so the two destructive flows look and behave identically: the opportunity name is echoed in bold, and the
// primary action stays disabled until the user types DELETE. On confirm it commits the delete then refetches
// (the row must leave the list — the delete's RANGE_DELETE targets a connection this page's query does not
// declare). formatMessage is injected rather than hooked so this module stays a plain, testable helper.
export function confirmDeleteOpportunity(
	node: OpportunityNode,
	onDone: () => void,
	formatMessage: (descriptor: {id: string}) => string
): void {
	showModal({
		type: MODAL_TYPE.CONFIRMATION,
		title: formatMessage({id: 'opportunities.delete_modal_title'}),
		description: (
			<div style={{whiteSpace: 'pre-line'}}>
				<FormattedMessage
					id="opportunities.delete_modal_description"
					values={{
						opportunityName: node.name ?? '',
						confirmText: SOCKET_ACTION.DELETE,
						b: (chunks: React.ReactNode) => <b>{chunks}</b>,
					}}
				/>
			</div>
		),
		modalSize: Modal.SIZE.MEDIUM,
		confirmPhrase: SOCKET_ACTION.DELETE,
		includeDefaultMessage: false,
		confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.DELETE,
		callbackPositive: () => util.CommitMutation(DeleteProjectMutation, {id: node.id, projectGroupId: null}, onDone),
	});
}
